<template>
  <div class="chat-input-field">
    <div 
      :class="{
        'active': !!(error || files.length),
        'container-chat-input-field': true
      }"
    >
      <div 
        v-if="error || files.length"
        class="container-file-item"
      >
        <CalicoFileItem
          v-for="(file, index) in files"
          :key="index"
          completed
          :name="file?.name"
          dark
          @trash="trashFileItem(index)"
        />
        <span 
          v-if="error"
          class="error body--medium"
        >
          {{ error }}
        </span>
        <span class="divider" />
      </div>
      <div class="container-input">
        <div class="custom-textarea-wrapper">
          <textarea
            ref="chatInputField"
            v-model="message"
            class="custom-textarea body--medium"
            placeholder="Ask BetaScore"
            rows="1"
            :disabled="disabled"
            v-bind="filteredAttrs"
            @keydown.enter="handleKeyDown"
            @input="adjustTextareaHeight"
          />
          <div class="buttons-container">
            <div style="width: fit-content;">
              <button
                :class="['button-secondary-alt', 'button-secondary-alt-icon--xsmall', { 'selected': isFileMenuOpen }]"
                :disabled="disabled" 
                @click="toggleFileMenu"
              >
                <i class="bi bi-plus" />
              </button>
              <div 
                v-if="isFileMenuOpen" 
                class="menu-container"
              >
                <CalicoNavbarMenu class="chatbox-menu">
                  <CalicoNavbarMenuItem @click="$refs.fileInput.click(); isFileMenuOpen = false">
                    <i class="bi bi-file-earmark-plus" />
                    <span class="file-upload-text body--medium">Upload from device</span>
                  </CalicoNavbarMenuItem>
                  <span class="divider" />
                  <div class="menu-info body--xsmall">
                    Accepted Formats:
                    <ul>
                      <li>.pdf, .docx, .xlsx, .csv, .txt</li>
                      <li>.jpg, .png</li>
                    </ul>
                  </div>
                </CalicoNavbarMenu>
              </div>
            </div>
            <div style="width: fit-content;">
              <button 
                class="button-utility-alt button-utility-alt-icon--xsmall" 
                :disabled="disabled || !message"
                @click="send"
              >
                <i class="bi bi-arrow-up" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <p class="disclaimer body--xsmall">
        BetaScore AI can make mistakes, review answers.
      </p>
      <input
        ref="fileInput"
        type="file"
        v-bind="$attrs"
        style="display: none"
        :disabled="disabled"
        multiple
        @change.prevent="(e) => $emit('upload', e.target.files)"
      >
    </div>
  </div>
</template>

<script>
import CalicoNavbarMenu from "@/components/calico/navbarMenu/index.vue";
import CalicoNavbarMenuItem from '@/components/calico/navbarMenuItem/index.vue';
import CalicoFileItem from "@/components/calico/fileItem/index.vue";

export default {
  name: 'CalicoChatInputField',
  components: {
    CalicoNavbarMenu,
    CalicoNavbarMenuItem,
    CalicoFileItem
  },
  props: {
    error: {
      type: String,
      default: ""
    },
    files: {
      type: Array,
      default: () => ([])
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      message: "",
      isMultiline: false,
      isFileMenuOpen: false
    };
  },
  computed: {
    filteredAttrs() {
      return { ...this.$attrs };
    }
  },
  mounted() {
    this.adjustTextareaHeight();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    focus(){
      this.$refs.chatInputField.focus()
    },
    send() {
      if (!this.message.trim()) return;
      this.$emit('send', this.message.trim());
      this.message = "";
      this.isMultiline = false;
      this.$nextTick(() => this.adjustTextareaHeight());
    },
    toggleFileMenu() {
      this.isFileMenuOpen = !this.isFileMenuOpen;
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isFileMenuOpen = false;
      }
    },
    trashFileItem(idx) {
      this.$emit("trash", idx);
      this.$refs.fileInput.value = "";
    },
    handleKeyDown(event) {
      if (event.shiftKey) return;
      event.preventDefault();
      this.send();
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.chatInputField;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
        this.isMultiline = textarea.scrollHeight > 50;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.chat-input-field {
  width: 100%;

  .active {
    background-color: $color-black;
  }

  .container-chat-input-field {
    @include stack-space($space-medium);
    @include inset-space($space-medium);
    width: 100%;

    &:not(.active) {
      background: $color-neutral-darkest;
    }

    border-radius: $space-large $space-large $space-none $space-none;

    &:focus-within {
      background-color: $color-black;
    }

    &:hover {
      background-color: $color-black;
    }

    .divider {
      width: 100%;
      border-bottom: 1px solid $color-neutral;
    }

    .menu-info {
      color: $color-text-secondary;

      ul {
        margin: $space-none $space-none $space-none $space-large;
        padding: 0;
        list-style-type: square;
      }
    }
    
    .container-file-item {
      @include stack-space($space-small);

      ::v-deep(.calico-file-item.dark) {
        background-color: transparent;
      }

      .divider {
        border-bottom: 1px solid $color-neutral-dark;
      }
    }

    .container-input {
      @include inline-space($space-none);
      justify-content: space-between;
      width: 100%;
    }

    .custom-textarea-wrapper {
      position: relative;
      width: 100%;
      display: flex;
      align-items: flex-end;

      .custom-textarea {
        width: 90%;
        max-height: 50vh;
        background: transparent;
        color: $color-white;
        border: $border-width-default solid transparent;
        border-radius: $radius-small;
        line-height: $line-height-medium;
        padding: $space-none;
        resize: none;
        overflow-y: scroll;
        scrollbar-width: none;

        &:focus {
          outline: none;
          border-color: $color-border-primary;
        }

        &::placeholder {
          color: $color-neutral;
        }
      }
    }

    .buttons-container {
      @include inline-space($space-medium);
      justify-content: center;
      align-items: center;
      position: relative;
    }

    .menu-container {
      position: absolute;
      bottom: 100%;
      right: 0;
      margin-bottom: $space-large;
      z-index: $z-index-dialog;
    }

    .button-secondary-alt-icon--xsmall.selected {
      background-color: $color-neutral-dark;
    }

    .file-upload-text {
      white-space: nowrap;
    }

    .bi {
      font-size: $icon-small;
    }

    .disclaimer {
      color: $color-text-secondary;
      margin: 0;
    }

    .error {
      color: $color-red;
    }
  }
}
</style>